/**
 * Kingsley standalone search modal.
 *
 * Self-contained: fetches the `kingsley-search` section via the Predictive Search
 * endpoint and injects the server-rendered HTML with a plain innerHTML replace.
 * No Section Rendering morph, no duplicate-id collisions, independent of the
 * theme's built-in predictive-search component.
 */
(function () {
  'use strict';

  var ROUTE =
    (window.Theme && window.Theme.routes && window.Theme.routes.predictive_search_url) ||
    '/search/suggest';

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  if (!('customElements' in window)) return;

  class KingsleySearchElement extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.qs-input');
      this.results = this.querySelector('[data-qs-results]');
      this.clearBtn = this.querySelector('.qs-clear');
      this.dialog = this.closest('dialog');

      if (!this.input || !this.results) return;

      // Snapshot the server-rendered empty state so we can restore it on clear.
      this.emptyHTML = this.results.innerHTML;

      this._onInput = debounce(this.handleInput.bind(this), 220);
      this.input.addEventListener('input', this._onInput);

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          this.input.value = '';
          this.clearBtn.hidden = true;
          this.reset();
          this.input.focus();
        });
      }

      // Focus the input whenever the dialog is opened.
      if (this.dialog) {
        this._mo = new MutationObserver(() => {
          if (this.dialog.hasAttribute('open')) {
            setTimeout(() => this.input && this.input.focus(), 60);
          }
        });
        this._mo.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
      }
    }

    disconnectedCallback() {
      if (this._mo) this._mo.disconnect();
      if (this._ctrl) this._ctrl.abort();
    }

    reset() {
      this.results.innerHTML = this.emptyHTML;
      this.results.scrollTop = 0;
    }

    handleInput() {
      var q = this.input.value.trim();
      if (this.clearBtn) this.clearBtn.hidden = q.length === 0;

      if (!q) {
        if (this._ctrl) this._ctrl.abort();
        this.reset();
        return;
      }

      var url = new URL(ROUTE, window.location.origin);
      url.searchParams.set('q', q);
      url.searchParams.set('section_id', 'kingsley-search');
      url.searchParams.set('resources[type]', 'product,collection,query');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');

      if (this._ctrl) this._ctrl.abort();
      this._ctrl = new AbortController();

      this.results.setAttribute('aria-busy', 'true');

      fetch(url.toString(), { signal: this._ctrl.signal })
        .then((res) => {
          if (!res.ok) throw new Error('Search request failed: ' + res.status);
          return res.text();
        })
        .then((html) => {
          var trimmed = (html || '').trim();
          this.results.innerHTML = trimmed || '<p class="qs-no-results">No results found.</p>';
          this.results.scrollTop = 0;
        })
        .catch((err) => {
          if (err && err.name === 'AbortError') return;
          console.error('[kingsley-search]', err);
        })
        .finally(() => {
          this.results.removeAttribute('aria-busy');
        });
    }
  }

  if (!customElements.get('kingsley-search')) {
    customElements.define('kingsley-search', KingsleySearchElement);
  }
})();
